// frontend/src/pages/TatmeenHierarchy/components/SSCCPackingTable.jsx

import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
    Table,
    Button,
    Tag,
    Card,
    Space,
    message,
    Spin,
    Popconfirm,
    Select,
    Tooltip,
} from "antd";
import {
    SendOutlined,
    ReloadOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    LoadingOutlined,
    ExclamationCircleOutlined,
} from "@ant-design/icons";

import { fetchSSCCPackingDetails, triggerPacking } from "../hierarchyThunk";

const { Option } = Select;

const SSCCPackingTable = ({ referenceType, referenceId }) => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [packingLoading, setPackingLoading] = useState(false);
    const [ssccData, setSsccData] = useState(null);
    const [packingType, setPackingType] = useState('flat');

    useEffect(() => {
        if (referenceType && referenceId) {
            loadSSCCDetails();
        }
    }, [referenceType, referenceId]);

    const loadSSCCDetails = async () => {
        setLoading(true);
        try {
            const result = await dispatch(fetchSSCCPackingDetails({
                reference_type: referenceType,
                reference_id: referenceId
            })).unwrap();

            setSsccData(result);

            if (result.packingType) {
                setPackingType(result.packingType);
            }
        } catch (error) {
            message.error(error.message || "Failed to load SSCC details");
        } finally {
            setLoading(false);
        }
    };

    const handlePack = async () => {
        setPackingLoading(true);
        try {
            const result = await dispatch(triggerPacking({
                reference_type: referenceType,
                reference_id: referenceId,
                packing_type: packingType
            })).unwrap();

            if (!result.error) {
                message.success(result.message || "Packing triggered successfully");
                await loadSSCCDetails();
            } else {
                message.error(result.message || "Packing failed");
            }
        } catch (error) {
            message.error(error.message || "Packing failed");
        } finally {
            setPackingLoading(false);
        }
    };

    // Helper: Get commission status config
    const getCommissionStatusConfig = (status) => {
        const config = {
            'not_started': { color: 'default', icon: null, label: 'Not Started' },
            'pending': { color: 'processing', icon: <LoadingOutlined />, label: 'Pending' },
            'sent': { color: 'success', icon: <CheckCircleOutlined />, label: 'Sent' },
            'failed': { color: 'error', icon: <CloseCircleOutlined />, label: 'Failed' }
        };
        return config[status] || config['not_started'];
    };

    // Child SSCCs columns (only if needed)
    const childColumns = [
        {
            title: 'Child SSCC',
            dataIndex: 'sscc_code',
            key: 'sscc_code',
            render: (text) => (
                <Space>
                    <span>{text}</span>
                    <Tag color="purple">Child</Tag>
                </Space>
            )
        },
        {
            title: 'Products',
            dataIndex: 'product_count',
            key: 'product_count',
            render: (count) => count || 0
        }
    ];

    // Main tracking columns - derived from tracking object
    const trackingColumns = [
        {
            title: 'SSCC Code',
            dataIndex: 'sscc_code',
            key: 'sscc_code',
            render: (text) => (
                <Space>
                    <span style={{ fontWeight: 'bold' }}>{text}</span>
                    <Tag color="blue">Root SSCC</Tag>
                </Space>
            )
        },
        {
            title: 'Commissioned',
            dataIndex: 'is_commissioned',
            key: 'is_commissioned',
            render: (value) => (
                value === 'yes'
                    ? <Tag color="success" icon={<CheckCircleOutlined />}>Yes</Tag>
                    : <Tag color="default" icon={<CloseCircleOutlined />}>No</Tag>
            )
        },
        {
            title: 'Commission Status',
            dataIndex: 'commission_status',
            key: 'commission_status',
            render: (status) => {
                const cfg = getCommissionStatusConfig(status);
                return (
                    <Tag color={cfg.color} icon={cfg.icon}>
                        {cfg.label}
                    </Tag>
                );
            }
        },
        {
            title: 'Failure Reason',
            dataIndex: 'commission_failure_reason',
            key: 'commission_failure_reason',
            render: (text) => (
                text ? (
                    <Tooltip title={text}>
                        <Tag color="error" icon={<ExclamationCircleOutlined />}>
                            {text.length > 50 ? text.substring(0, 50) + '...' : text}
                        </Tag>
                    </Tooltip>
                ) : '-'
            )
        },
        {
            title: 'Retry Count',
            dataIndex: 'commission_retry_count',
            key: 'commission_retry_count',
            render: (count) => count || 0
        },
        {
            title: 'Manual Action Required',
            dataIndex: 'commission_manual_action_required',
            key: 'commission_manual_action_required',
            render: (value) => (
                value === 'yes'
                    ? <Tag color="error" icon={<ExclamationCircleOutlined />}>Yes</Tag>
                    : <Tag color="success">No</Tag>
            )
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                const config = {
                    'pending': { color: 'warning', label: 'Pending' },
                    'active': { color: 'success', label: 'Active' },
                    'shipped': { color: 'blue', label: 'Shipped' },
                    'received': { color: 'cyan', label: 'Received' },
                    'unpacked': { color: 'green', label: 'Unpacked' },
                    'damaged': { color: 'error', label: 'Damaged' },
                    'stolen': { color: 'error', label: 'Stolen' },
                    'destroyed': { color: 'red', label: 'Destroyed' },
                    'recalled': { color: 'orange', label: 'Recalled' }
                };
                const cfg = config[status] || { color: 'default', label: status || '-' };
                return <Tag color={cfg.color}>{cfg.label}</Tag>;
            }
        },
        {
            title: 'Commissioned At',
            dataIndex: 'commissioned_at',
            key: 'commissioned_at',
            render: (date) => date ? new Date(date).toLocaleString() : '-'
        }
    ];

    // Loading state
    if (loading) {
        return (
            <Card style={{ marginTop: 16 }}>
                <div style={{ textAlign: 'center', padding: 20 }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 12 }}>Loading SSCC details...</div>
                </div>
            </Card>
        );
    }

    // No data state
    if (!ssccData || !ssccData.rootSSCC) {
        return null;
    }

    const tracking = ssccData.tracking;
    const trackingData = tracking ? [tracking] : [];
    const isCommissioned = tracking?.is_commissioned === 'yes';
    const hasFailedCommission = tracking?.commission_status == 'failed';
    const isAlreadyPacked = isCommissioned && !hasFailedCommission;
    console.log(hasFailedCommission, "isAlreadyPacked");
    console.log(isCommissioned, "isAlreadyPacked1");


    return (
        <Card
            title={
                <Space>
                    <span>SSCC Packing Details</span>
                    {ssccData.voucher?.number && (
                        <Tag color="blue">
                            {ssccData.voucher.type === 'sale' ? 'Sale' : 'Return'}: {ssccData.voucher.number}
                        </Tag>
                    )}
                    <Tag color="geekblue">Root: {ssccData.rootSSCC}</Tag>
                    {ssccData.summary?.childCount > 0 && (
                        <Tag color="purple">Children: {ssccData.summary.childCount}</Tag>
                    )}
                    {isCommissioned && (
                        <Tag color="success" icon={<CheckCircleOutlined />}>
                            Commissioned
                        </Tag>
                    )}
                    {hasFailedCommission && (
                        <Tag color="error" icon={<CloseCircleOutlined />}>
                            Failed - Retry Available
                        </Tag>
                    )}
                    {ssccData.summary?.tatmeenManualActionRequired === 'yes' && (
                        <Tag color="error" icon={<ExclamationCircleOutlined />}>
                            Manual Action Required
                        </Tag>
                    )}
                </Space>
            }
            style={{ marginTop: 16 }}
            extra={
                <Space>
                    {/* Only show packing type selector if there are children */}

                    <Select
                        value={packingType}
                        onChange={setPackingType}
                        style={{ width: 140 }}
                        disabled={packingLoading || isAlreadyPacked}
                    >
                        <Option value="flat">Flat Packing</Option>
                        <Option value="hierarchical">Hierarchical Packing</Option>
                    </Select>


                    <Popconfirm
                        title="Are you sure you want to trigger packing?"
                        description="This will send the SSCC hierarchy to Tatmeen for commissioning."
                        onConfirm={handlePack}
                        okText="Yes, Pack"
                        cancelText="Cancel"
                        disabled={packingLoading || isAlreadyPacked}
                    >
                        <Button
                            type="primary"
                            icon={<SendOutlined />}
                            loading={packingLoading}
                            disabled={isAlreadyPacked}
                        >
                            {isAlreadyPacked ? 'Already Commissioned' : 'Pack SSCC'}
                        </Button>
                    </Popconfirm>

                    <Button
                        icon={<ReloadOutlined />}
                        onClick={loadSSCCDetails}
                        loading={loading}
                    >
                        Refresh
                    </Button>
                </Space>
            }
        >
            {/* Root SSCC Tracking */}
            <div style={{ marginBottom: 16 }}>
                <Tag color="blue" style={{ marginBottom: 8 }}>Root SSCC</Tag>
                <Table
                    columns={trackingColumns}
                    dataSource={trackingData}
                    rowKey="sscc_code"
                    pagination={false}
                    size="small"
                    bordered
                />
            </div>

            {/* Child SSCCs (Sales only) */}
            {ssccData.childSSCCs && ssccData.childSSCCs.length > 0 && (
                <div>
                    <Tag color="purple" style={{ marginBottom: 8 }}>
                        Child SSCCs ({ssccData.childSSCCs.length})
                    </Tag>
                    <Table
                        columns={childColumns}
                        dataSource={ssccData.childSSCCs}
                        rowKey="sscc_code"
                        pagination={false}
                        size="small"
                        bordered
                    />
                </div>
            )}

            {/* Summary */}
            {ssccData.summary && (
                <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
                    <Space>
                        <Tag color="geekblue">
                            Total SSCCs: {ssccData.summary.totalSSCCs}
                        </Tag>
                        {ssccData.summary.childCount > 0 && (
                            <Tag color="purple">
                                Children: {ssccData.summary.childCount}
                            </Tag>
                        )}
                        {ssccData.summary.hasParent && (
                            <Tag color="blue">Has Parent SSCC</Tag>
                        )}
                        {tracking && (
                            <>
                                <Tag color={isCommissioned ? 'success' : 'default'}>
                                    {isCommissioned ? '✅ Commissioned' : '⏳ Not Commissioned'}
                                </Tag>
                                {tracking.commission_retry_count > 0 && (
                                    <Tag color="orange">
                                        Retries: {tracking.commission_retry_count}
                                    </Tag>
                                )}
                            </>
                        )}
                        {ssccData.summary.tatmeenSentStatus && (
                            <Tag color={
                                ssccData.summary.tatmeenSentStatus === 'sent' ? 'success' :
                                    ssccData.summary.tatmeenSentStatus === 'failed' ? 'error' :
                                        'warning'
                            }>
                                Tatmeen: {ssccData.summary.tatmeenSentStatus}
                            </Tag>
                        )}
                    </Space>
                </div>
            )}
        </Card>
    );
};

export default SSCCPackingTable;